import React, { forwardRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import type { Attention, WorkItem } from '../api/workItems';
import { sourceKeyLabel } from '../utils/sourceKeyLabel';
import {
    currentSourceRuleAttention,
    sourceTeachHeadline,
} from './sourceTeachEligibility';

export interface WorkItemSourceTeachStripProps {
    item: WorkItem;
    saving: boolean;
    onKeepAsTask: () => void;
    onWatchSource: () => void;
    onIgnoreSource: () => void;
}

const verbButtonSx = {
    textTransform: 'none' as const,
    fontWeight: 700,
    fontSize: '0.78rem',
    minHeight: 36,
    px: 1.25,
};

const WorkItemSourceTeachStrip = forwardRef<HTMLDivElement, WorkItemSourceTeachStripProps>(
    function WorkItemSourceTeachStrip(
        { item, saving, onKeepAsTask, onWatchSource, onIgnoreSource },
        ref,
    ) {
        const label = sourceKeyLabel(item.source_key || '');
        const headline = sourceTeachHeadline(item, label);
        const current = currentSourceRuleAttention(item);

        const verbs: Array<{
            attention: Attention;
            testId: string;
            label: string;
            onClick: () => void;
        }> = [
            { attention: 'action', testId: 'WB_DRAWER_KEEP_AS_TASK', label: 'Keep as task', onClick: onKeepAsTask },
            { attention: 'watch', testId: 'WB_DRAWER_WATCH_SOURCE', label: 'Watch source', onClick: onWatchSource },
            { attention: 'ignore', testId: 'WB_DRAWER_IGNORE_SOURCE', label: 'Ignore source', onClick: onIgnoreSource },
        ];

        return (
            <Box
                ref={ref}
                tabIndex={-1}
                data-testid="WB_DRAWER_SOURCE_TEACH_STRIP"
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.25,
                    borderBottom: '1px solid var(--color-border)',
                    bgcolor: 'var(--color-bg-secondary)',
                    outline: 'none',
                    '&:focus-visible': {
                        boxShadow: 'inset 0 0 0 2px var(--color-accent)',
                    },
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        fontWeight: 700,
                        color: 'var(--color-text-secondary)',
                        mb: 0.75,
                    }}
                >
                    {headline}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {verbs.map((verb) => (
                        <Button
                            key={verb.attention}
                            size="small"
                            variant={current === verb.attention ? 'contained' : 'outlined'}
                            disabled={saving || current === verb.attention}
                            onClick={verb.onClick}
                            data-testid={verb.testId}
                            sx={verbButtonSx}
                        >
                            {verb.label}
                        </Button>
                    ))}
                </Box>
            </Box>
        );
    },
);

export default WorkItemSourceTeachStrip;
